import React, { useState } from 'react';
import { FadeInUp } from './FadeInUp';
import { CheckCircle2, DownloadIcon, Lock, User, Mail, Phone, DollarSign } from 'lucide-react';
interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  investment: string;
}
export function LeadForm() {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    investment: ''
  });
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const validate = () => {
    const newErrors: Partial<FormData> = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'El nombre es requerido';
    if (!formData.lastName.trim()) newErrors.lastName = 'El apellido es requerido';
    if (!formData.email.trim()) {
      newErrors.email = 'El correo es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Correo electrónico inválido';
    }
    if (!formData.phone.trim()) newErrors.phone = 'El teléfono es requerido';
    if (!formData.investment) newErrors.investment = 'Selecciona una opción';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      // Fire Meta Pixel Lead event
      if (typeof window !== 'undefined' && (window as any).fbq) {
        ;
        (window as any).fbq('track', 'Lead', {
          content_name: 'IUL Asesoria Gratuita',
          investment_range: formData.investment
        });
      }
      // Send data to webhook
      fetch('https://services.leadconnectorhq.com/hooks/mm4XWU5oWtYb7RcmqRCk/webhook-trigger/c7aqJbtIOxIV9Yua7mos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          investmentRange: formData.investment
        })
      }).then(() => setIsSubmitted(true)).catch(() => setIsSubmitted(true));
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when typing
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  return <section id="lead-form" className="bg-deep-blue py-24 lg:py-32 relative overflow-hidden" data-id="element-56">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-57">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-vitality-green opacity-10 rounded-full blur-[100px]" data-id="element-58"></div>
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-blue-400 opacity-10 rounded-full blur-[100px]" data-id="element-59"></div>
      </div>

      <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10" data-id="element-60">
        <FadeInUp data-id="element-61">
          <div className="text-center mb-16" data-id="element-62">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight" data-id="element-63">
              Da el primer paso hacia tu tranquilidad financiera
            </h2>
            <p className="text-lg md:text-xl text-blue-100/90 max-w-2xl mx-auto font-light" data-id="element-64">
              Deja tus datos para recibir una asesoría personalizada. Descubre
              si el IUL es la solución perfecta para ti.
            </p>
          </div>
        </FadeInUp>

        <FadeInUp delay={0.2} data-id="element-65">
          <div className="bg-white rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.3)] p-8 md:p-12 lg:p-14 relative" data-id="element-66">
            {isSubmitted ? <div className="text-center py-12" data-id="element-67">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-green-50 rounded-full mb-8" data-id="element-68">
                  <CheckCircle2 className="w-12 h-12 text-vitality-green" data-id="element-69" />
                </div>
                <h3 className="text-3xl font-bold text-deep-blue mb-4" data-id="element-70">
                  ¡Gracias por solicitar tu asesoría!
                </h3>
                <p className="text-xl text-gray-600 mb-10" data-id="element-71">
                  En breve uno de nuestros expertos te contactará para agendar
                  tu sesión.
                </p>

                <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-8 max-w-md mx-auto text-left relative overflow-hidden" data-id="element-72">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100/50 rounded-full blur-2xl -mr-16 -mt-16" data-id="element-73"></div>
                  <p className="text-deep-blue font-bold text-xl mb-3 relative z-10" data-id="element-74">
                    📘 Mientras esperas, ¡prepárate!
                  </p>
                  <p className="text-gray-600 mb-8 text-base leading-relaxed relative z-10" data-id="element-75">
                    Descarga esta guía y descubre cómo el IUL puede ser tu plan
                    3 en 1 para proteger a tu familia, hacer crecer tus ahorros
                    y jubilarte con tranquilidad.
                  </p>
                  <a href="https://gamma.app/docs/IUL-El-Plan-3-en-1-para-Proteger-a-tu-Familia-y-Jubilarte-con-Dig-hg4ru7rugrj9b1x" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 bg-vitality-green hover:bg-vitality-green-hover text-white font-semibold px-8 py-4 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 relative z-10 w-full" data-id="element-76">
                    <DownloadIcon className="w-5 h-5" data-id="element-77" />
                    Descargar guía
                  </a>
                </div>
              </div> : <form onSubmit={handleSubmit} className="space-y-8" data-id="element-78">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-79">
                  <div className="relative" data-id="element-80">
                    <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-81">
                      Nombre
                    </label>
                    <div className="relative" data-id="element-82">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-83">
                        <User className="h-5 w-5 text-gray-400" data-id="element-84" />
                      </div>
                      <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} className={`w-full pl-12 pr-4 py-4 rounded-xl border-2 ${errors.firstName ? 'border-red-500 bg-red-50' : 'border-gray-100 bg-gray-50 hover:border-gray-200'} focus:bg-white focus:ring-0 focus:border-vitality-green outline-none transition-all text-gray-800`} placeholder="Tu nombre" data-id="element-85" />
                    </div>
                    {errors.firstName && <p className="mt-2 text-sm text-red-500 font-medium" data-id="element-86">
                        {errors.firstName}
                      </p>}
                  </div>
                  <div className="relative" data-id="element-87">
                    <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-88">
                      Apellido
                    </label>
                    <div className="relative" data-id="element-89">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-90">
                        <User className="h-5 w-5 text-gray-400" data-id="element-91" />
                      </div>
                      <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} className={`w-full pl-12 pr-4 py-4 rounded-xl border-2 ${errors.lastName ? 'border-red-500 bg-red-50' : 'border-gray-100 bg-gray-50 hover:border-gray-200'} focus:bg-white focus:ring-0 focus:border-vitality-green outline-none transition-all text-gray-800`} placeholder="Tu apellido" data-id="element-92" />
                    </div>
                    {errors.lastName && <p className="mt-2 text-sm text-red-500 font-medium" data-id="element-93">
                        {errors.lastName}
                      </p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-94">
                  <div className="relative" data-id="element-95">
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-96">
                      Correo electrónico
                    </label>
                    <div className="relative" data-id="element-97">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-98">
                        <Mail className="h-5 w-5 text-gray-400" data-id="element-99" />
                      </div>
                      <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={`w-full pl-12 pr-4 py-4 rounded-xl border-2 ${errors.email ? 'border-red-500 bg-red-50' : 'border-gray-100 bg-gray-50 hover:border-gray-200'} focus:bg-white focus:ring-0 focus:border-vitality-green outline-none transition-all text-gray-800`} placeholder="tu@correo.com" data-id="element-100" />
                    </div>
                    {errors.email && <p className="mt-2 text-sm text-red-500 font-medium" data-id="element-101">
                        {errors.email}
                      </p>}
                  </div>
                  <div className="relative" data-id="element-102">
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-103">
                      Teléfono
                    </label>
                    <div className="relative" data-id="element-104">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-105">
                        <Phone className="h-5 w-5 text-gray-400" data-id="element-106" />
                      </div>
                      <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className={`w-full pl-12 pr-4 py-4 rounded-xl border-2 ${errors.phone ? 'border-red-500 bg-red-50' : 'border-gray-100 bg-gray-50 hover:border-gray-200'} focus:bg-white focus:ring-0 focus:border-vitality-green outline-none transition-all text-gray-800`} placeholder="(555) 123-4567" data-id="element-107" />
                    </div>
                    {errors.phone && <p className="mt-2 text-sm text-red-500 font-medium" data-id="element-108">
                        {errors.phone}
                      </p>}
                  </div>
                </div>

                <div className="relative" data-id="element-109">
                  <label htmlFor="investment" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-110">
                    ¿Qué monto estarías dispuesto a invertir mensualmente?
                  </label>
                  <div className="relative" data-id="element-111">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-112">
                      <DollarSign className="h-5 w-5 text-gray-400" data-id="element-113" />
                    </div>
                    <select id="investment" name="investment" value={formData.investment} onChange={handleChange} className={`w-full pl-12 pr-4 py-4 rounded-xl border-2 ${errors.investment ? 'border-red-500 bg-red-50' : 'border-gray-100 bg-gray-50 hover:border-gray-200'} focus:bg-white focus:ring-0 focus:border-vitality-green outline-none transition-all text-gray-800 appearance-none`} data-id="element-114">
                      <option value="" data-id="element-115">Selecciona una opción</option>
                      <option value="150-300" data-id="element-116">$150 - $300</option>
                      <option value="300-500" data-id="element-117">$300 - $500</option>
                      <option value="500-800" data-id="element-118">$500 - $800</option>
                      <option value="800+" data-id="element-119">Más de $800</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none" data-id="element-120">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-121">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" data-id="element-122"></path>
                      </svg>
                    </div>
                  </div>
                  {errors.investment && <p className="mt-2 text-sm text-red-500 font-medium" data-id="element-123">
                      {errors.investment}
                    </p>}
                </div>

                <div className="pt-4" data-id="element-124">
                  <button type="submit" className="w-full bg-vitality-green hover:bg-vitality-green-hover text-white font-bold text-xl py-5 rounded-xl transition-all duration-300 shadow-[0_8px_30px_rgb(45,159,93,0.3)] hover:shadow-[0_8px_30px_rgb(45,159,93,0.5)] transform hover:-translate-y-1 flex items-center justify-center gap-2" data-id="element-125">
                    Quiero mi asesoría
                  </button>

                  <div className="flex items-center justify-center gap-2 mt-6 text-gray-500" data-id="element-126">
                    <Lock className="w-4 h-4" data-id="element-127" />
                    <span className="text-sm font-medium" data-id="element-128">
                      Tus datos están 100% seguros
                    </span>
                  </div>
                </div>

                <p className="text-xs text-center text-gray-400 mt-6 leading-relaxed" data-id="element-129">
                  Al dejar tus datos, recibirás una asesoría personalizada para
                  saber si el IUL es el producto ideal para ti y tu familia.
                </p>
              </form>}
          </div>
        </FadeInUp>
      </div>
    </section>;
}